import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useReportQueue } from '@hooks/useReportQueue'
import { useSchedulesQuery } from '@hooks/useSchedules'
import useTicker from '@hooks/useTicker'
import { ACTIVE_STATES, MAX_RUN_MS, NO_RESULT_STATES, QUEUE_PATH, failureAdvice, jobState, resultExpiry } from '@config/queueConfig'
import { fmtDuration, fmtElapsed, fmtInFuture, fmtRowCount, fmtTime, fmtWhen } from './queue/queueFormat'
import { expiryNote } from './queue/resultRetention'
import { Spinner } from './queue/queueIcons'
import { STATE_ICONS } from './queue/stateIcons'

const REPORT_JOBS = import.meta.env.VITE_REPORT_JOBS === 'true'

/*
 * Himaad's Section 6 shows four tabs — All / Running / Ready / Scheduled — and
 * drops "Didn't finish".
 *
 * **"Didn't finish" is KEPT, and Scheduled is added after it.** Removing it would
 * be a findability regression dressed as a restyle: `queueConfig.js` records the
 * design-review reasoning that failure is the state users care about most, and
 * this panel would then be the only surface with no way to filter to it. Five tabs
 * is a smaller cost than that.
 */
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'ready', label: 'Ready' },
  { key: 'failed', label: "Didn't finish" },
  { key: 'scheduled', label: 'Scheduled' },
]

function inTab(job, tab) {
  if (tab === 'all') return true
  if (tab === 'scheduled') return job.effectiveState === 'scheduled'
  if (tab === 'running') return ACTIVE_STATES.has(job.effectiveState)
  // Same rule as the Queue page's `matchesTab` (QA, 2026-09-03): a run the user
  // stopped produced no result, so it belongs under "Didn't finish". The bare
  // `=== tab` fallthrough put `cancelled` in no tab but All.
  if (tab === 'failed') return NO_RESULT_STATES.has(job.effectiveState)
  return job.effectiveState === tab
}

/**
 * Screen 7 — the notification center (the header bell).
 *
 * The model: the BELL IS THE PEEK, the Queue tab is the full page. This is the
 * former MyReportsTray restructured — tab filters over the data useMyReports
 * already returns, read/unread, real name resolution, and a footer link into the
 * queue. Not a new subsystem.
 *
 * Ranked above the schedules calendar for MVP in the design review, on the
 * grounds that it's more central to the experience.
 *
 * The badge turns GREEN when something is ready (not just blue for running), so
 * "ready" doesn't get lost — the stated risk was this becoming too hidden for
 * something as important as reports.
 */
const NotificationCenter = () => {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('all')
  const panelRef = useRef(null)
  const navigate = useNavigate()

  const [confirmClear, setConfirmClear] = useState(false)

  const { allJobs, loading, dismiss, dismissAll, markRead, markAllRead, runningCount, readyCount, unreadCount, error: listError, refresh } =
    useReportQueue({ enabled: REPORT_JOBS })

  /*
   * A 1 Hz clock, LOCAL to this panel and only while it is open.
   *
   * The elapsed subline needs second granularity, and `useMyReports` deliberately
   * exposes only a 30-second `now` — that clock feeds expiry derivation for every
   * consumer of the queue, so speeding it up would re-render the queue page and
   * the tray every second. Gating on `open` means a closed bell costs nothing,
   * which is the state it is in almost all the time.
   */
  const panelNow = useTicker(open, 1000)

  /*
   * Schedule names, for the provenance line ("from Monday A/R pack").
   *
   * The same query the Queue page and the schedules page use — one key, one
   * freshness policy — so this adds no new fetch on a page that already shows the
   * chip. `enabled` follows the flag: with jobs off the bell renders nothing at
   * all, so there is nothing to name.
   *
   * A row whose schedule is not in this map falls back to "from a schedule"
   * rather than omitting the fact or guessing a name. The map can legitimately
   * miss: the schedule may have been deleted, or belong to a colleague under a
   * scope this user is not reading.
   */
  const schedulesQuery = useSchedulesQuery({ enabled: REPORT_JOBS })
  const scheduleNames = useMemo(() => {
    const m = new Map()
    for (const s of schedulesQuery.schedules || []) {
      if (s?.id != null && s.name) m.set(String(s.id), s.name)
    }
    return m
  }, [schedulesQuery.schedules])

  // Fire a browser notification when a report finishes while the user is on
  // another tab. The poll continues when backgrounded, so we can catch the
  // running→ready/failed transition; only notify when the tab isn't focused —
  // a focused user already sees this panel update.
  const prevStatesRef = useRef(new Map())
  useEffect(() => {
    if (!REPORT_JOBS || typeof Notification === 'undefined') return
    if (Notification.permission === 'default' && allJobs.some((j) => ACTIVE_STATES.has(j.effectiveState))) {
      Notification.requestPermission().catch(() => {})
    }
    for (const j of allJobs) {
      const prev = prevStatesRef.current.get(j.id)
      const finished = j.effectiveState === 'ready' || j.effectiveState === 'failed'
      if (prev && ACTIVE_STATES.has(prev) && finished && Notification.permission === 'granted' && !document.hasFocus()) {
        const body = j.effectiveState === 'ready'
          ? `${j.displayName} is ready${j.rowCount != null ? ` · ${fmtRowCount(j.rowCount)}` : ''}.`
          : `${j.displayName} didn’t finish.`
        try {
          const n = new Notification('Report Center', { body, tag: `cb2-report-${j.id}` })
          if (j.effectiveState === 'ready' && j.reportId) {
            n.onclick = () => { window.focus(); n.close(); navigate(`/reports/${j.reportId}?jobId=${j.id}`) }
          }
        } catch { /* notifications unavailable */ }
      }
    }
    prevStatesRef.current = new Map(allJobs.map((j) => [j.id, j.effectiveState]))
  }, [allJobs, navigate])

  useEffect(() => {
    if (!open) return undefined
    const onDoc = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false) }
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onEsc)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc) }
  }, [open])

  /*
   * CC-53101 — this tray is the SECOND consumer of the queue, and it derives its
   * counts LOCALLY rather than taking the hook's, so gating them in `useReportQueue`
   * does not reach here. That asymmetry is the whole reason this residual survived
   * CC-53047: the page was fixed and the bell was not.
   *
   * On a failed read `allJobs` is `[]`, so every one of these filters returns 0 and
   * the panel rendered "All 0 / Running 0 / Ready 0 / Didn't finish 0" directly under
   * its own "We couldn't load your reports — this is not an empty list." `null` means
   * "no number to show"; the tab badge renders an em dash for it.
   */
  const counts = useMemo(() => {
    if (listError) return null
    const c = {}
    for (const t of TABS) c[t.key] = allJobs.filter((j) => inTab(j, t.key)).length
    return c
  }, [allJobs, listError])

  const visible = useMemo(() => allJobs.filter((j) => inTab(j, tab)), [allJobs, tab])

  if (!REPORT_JOBS) return null

  // The badge counts what is genuinely NEW for you — ready results you haven't
  // opened. It used to count `runningCount || readyCount`, which read-state could
  // never clear, so "Mark all as read" left the number sitting there.
  //
  // In-flight work still needs a signal, so when there's nothing unread but
  // something is running we show a dot with NO number: activity, not a to-do.
  const showCount = unreadCount > 0
  const showActivityDot = !showCount && runningCount > 0

  return (
    <div className="relative" ref={panelRef}>
      <button
        aria-label={`Report activity${unreadCount ? `, ${unreadCount} unread` : ''}${runningCount ? `, ${runningCount} running` : ''}${readyCount ? `, ${readyCount} ready` : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-cb-light focus:outline-none focus-visible:ring-2 focus-visible:ring-cb-accent/40"
      >
        {/* Bell, replacing the old bar-chart glyph so it reads as notifications
            rather than charts. */}
        <svg className="h-5 w-5 text-cb-navy" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.4-1.6A2 2 0 0118 14V11a6 6 0 10-12 0v3a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {/*
          * ADA fix (spec 111). The badge was `bg-cb-green` — white on `#34c475`
          * measures **2.258:1**, which fails SC 1.4.3 outright for a 10px bold
          * count, and 10px is not large text under any reading of the criterion.
          * `--color-cb-green-dark` (#005e37) with white measures **7.906:1**.
          *
          * The badge is the only thing that says how many results are waiting, so
          * it is text carrying information, not decoration. `cb-green` stays
          * correct where it is a FILL behind dark text or an icon; it is only
          * white-on-green that fails.
          */}
        {showCount && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-cb-green-dark px-1 text-[10px] font-bold text-white">
            {unreadCount}
          </span>
        )}
        {showActivityDot && (
          <span
            aria-hidden="true"
            className="absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-cb-accent-text ring-2 ring-white"
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Report activity"
          /*
           * Width is DERIVED from the tab row, not chosen (QA, 2026-09-03).
           *
           * Measured on the deployed build at 392px: the tab row's `scrollWidth`
           * was **457px** against a `clientWidth` of 390, so the fifth tab
           * ("Scheduled") started at x=1230.6 with width 110 inside a panel whose
           * right edge was 1274 — **66px of it clipped**, silently, by the
           * `overflow-hidden` here. The fifth tab arrived in spec 112; nothing
           * re-measured the row that had to hold it.
           *
           * 457 + the row's own `px-2` (16) = 473 minimum. `whitespace-nowrap` on
           * the tabs below takes it under that again (it also stops "Didn't
           * finish" wrapping, which was making the row 51px tall instead of ~33),
           * so 496 leaves real slack for a three-digit count without a
           * horizontally-scrolling tab row inside a dropdown.
           *
           * `max-w-[calc(100vw-2rem)]` so a narrow window clamps instead of
           * pushing the panel off-screen.
           */
          className="absolute right-0 top-full z-50 mt-2 w-[496px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg"
        >
          <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <span className="text-sm font-semibold text-cb-navy">Report activity</span>
            <span className="flex items-center gap-2.5">
              {unreadCount > 0 && (
                <button type="button" onClick={markAllRead} className="btn-cb-tertiary">Mark all as read</button>
              )}
              {allJobs.length > 0 && (
                confirmClear ? (
                  /* Inline two-step rather than a ConfirmModal: this panel closes
                     on outside-mousedown, so a modal layered over it fights its own
                     dismissal. The wording states what Clear all does NOT do —
                     same honesty rule as "Dismiss", since DELETE removes the row
                     without stopping the work (real cancel is CC-50150). */
                  <span className="flex items-center gap-2" role="alert">
                    <span className="text-[11px] text-gray-500">Clear {allJobs.length}? Running work continues.</span>
                    <button
                      type="button"
                      onClick={() => { dismissAll(); setConfirmClear(false) }}
                      className="btn-cb-tertiary font-semibold text-red-600"
                    >
                      Yes, clear
                    </button>
                    <button type="button" onClick={() => setConfirmClear(false)} className="btn-cb-tertiary">Keep</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setConfirmClear(true)} className="btn-cb-tertiary">Clear all</button>
                )
              )}
            </span>
          </div>

          <div className="flex border-b border-gray-200 px-2" role="tablist" aria-label="Filter report activity">
            {TABS.map((t) => {
              const on = t.key === tab
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTab(t.key)}
                  /* `whitespace-nowrap`: "Didn't finish" was wrapping to two
                     lines, which doubled the row's height and made it look like
                     a layout bug rather than a width one. */
                  className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 py-2 text-xs font-medium transition-colors ${
                    on ? 'border-cb-accent-text font-semibold text-cb-accent-text' : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {t.label}
                  <span className={`rounded-full px-1.5 py-px text-[10px] font-bold ${on ? 'bg-cb-accent/15 text-cb-accent-text' : 'bg-gray-100 text-gray-600'}`}>
                    {counts == null
                      ? <span aria-label="count unavailable">—</span>
                      : (counts[t.key] ?? 0)}
                  </span>
                </button>
              )
            })}
          </div>

          {/*
            * Taller, and capped against the viewport rather than flat (QA asked
            * for "a bit longer as well"). Measured on the deployed build: the
            * scroller was 330px tall against a 5,349px scrollHeight, so it showed
            * roughly one row in sixteen. `min()` keeps a short laptop viewport
            * from pushing the panel below the fold — the panel opens at y≈58, so
            * 60vh of list plus ~80px of chrome still fits at 768px.
            */}
          <div className="max-h-[min(440px,60vh)] overflow-y-auto">
            {loading && visible.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-gray-500">Loading…</p>
            )}
            {/* CC-53047 — this tray is the SECOND consumer of the same hook. Without
                this branch the bell would keep saying "Nothing here yet." while the
                Queue page correctly reported a failed read, which is the same lie in a
                quieter place. Gated ahead of the empty state, and the empty state is
                gated on !listError, so the two can never both render. */}
            {listError && (
              <div role="alert" className="px-4 py-6 text-center">
                <p className="text-sm font-semibold text-gray-900">We couldn&apos;t load your reports</p>
                <p className="mt-1 text-[11.5px] text-gray-500">This is not an empty list — the read failed.</p>
                <button type="button" onClick={refresh} className="btn-cb-toolbar mt-2.5">Try again</button>
              </div>
            )}
            {!loading && !listError && visible.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-gray-500">Nothing here yet.</p>
            )}
            {visible.map((j) => {
              const vocab = jobState(j.effectiveState)
              const Icon = STATE_ICONS[vocab.icon] || STATE_ICONS.clock
              const active = ACTIVE_STATES.has(j.effectiveState)
              // Use the already-derived effectiveState, not a fresh derive —
              // an expired job must never render a live "View report" link.
              const canView = j.effectiveState === 'ready' && j.reportId
              const unread = !j.read && j.effectiveState === 'ready'
              return (
                <div
                  key={j.id}
                  className={`flex items-start gap-2.5 border-b border-gray-100 px-4 py-3 last:border-b-0 ${unread ? 'bg-cb-green/[0.05]' : ''}`}
                >
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${vocab.badge}`}>
                    {active ? <Spinner className="w-3.5 h-3.5" /> : <Icon />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.8px] font-semibold text-gray-900">
                      {j.displayName}
                      {j.effectiveState === 'ready' ? ' is ready' : j.effectiveState === 'failed' ? ' didn’t finish' : ''}
                    </div>
                    {/* A per-row handle for the subline. It is assembled from
                        several text nodes, so a text matcher hits this div AND
                        every ancestor whose textContent contains the phrase —
                        ambiguous by construction rather than by accident. */}
                    <div data-testid={`notif-subline-${j.id}`} className="mt-1 text-[11.5px] text-gray-500">
                      {/*
                        * Himaad's Section 6 row 2: `Running · 1m 12s elapsed ·
                        * started 2:58 PM`, not `Running · 0%`.
                        *
                        * The percentage came from CB's own status string and is
                        * frequently 0 for an entire run, so the old subline read
                        * "Running · 0%" on a healthy report — which looks stuck.
                        * Elapsed and a start time are both facts we hold locally
                        * and cannot be wrong.
                        *
                        * `panelNow` ticks at 1 Hz and is LOCAL to this open panel.
                        * `useMyReports` deliberately exposes only a 30-second
                        * clock, because that clock feeds expiry derivation for
                        * every consumer — speeding it up would re-render the whole
                        * queue every second. See the note at `panelNow`.
                        */}
                      {active && (
                        <>
                          Running · {fmtElapsed(j.createdAt, panelNow)} elapsed · started {fmtTime(j.createdAt)}
                        </>
                      )}
                      {j.effectiveState === 'ready' && (
                        <>
                          {/* Spec 167: `fmtRowCount`, so one row reads "1 row", not "1 rows". */}
                          {fmtRowCount(j.rowCount)} · {fmtDuration(j.durationMs)} · {fmtTime(j.finishedAt ?? j.createdAt)}
                          {/* Spec 152: to the instant the row EXPIRES on (`resultExpiry` —
                              the earlier of `expires_at` and the results bucket's age),
                              never the row's own week, which outlives prod's bucket. And
                              for a live schedule's run, as a BOUND ("up to 3d left"): its
                              "last N" count can prune it sooner (`expiryNote`). */}
                          {resultExpiry(j) != null ? ` · ${expiryNote(j)}` : ''}
                          {/*
                            * Provenance — "from Monday A/R pack" vs "queued
                            * manually" (Himaad's rows 1 and 4). Possible only
                            * because `scheduleId` now reaches the client; the name
                            * is resolved from the schedules list the app already
                            * fetches, and falls back to the neutral phrasing when
                            * that lookup has not landed rather than guessing.
                            */}
                          {j.scheduleId != null
                            ? ` · ${scheduleNames.get(String(j.scheduleId)) ? `from ${scheduleNames.get(String(j.scheduleId))}` : 'from a schedule'}`
                            : ' · queued manually'}
                        </>
                      )}
                      {/*
                        * Aligned with the queue and the drawer, which both already
                        * say this. The bell's flat "Stopped without returning data"
                        * threw away the diagnosis: when a run hit the cap, the
                        * duration IS the explanation. Derived from MAX_RUN_MS so
                        * the three surfaces cannot drift.
                        */}
                      {j.effectiveState === 'failed' && (
                        <span className="text-rc-danger-text">
                          {j.durationMs != null && j.durationMs >= MAX_RUN_MS - 2000
                            ? `Stopped at the ${Math.round(MAX_RUN_MS / 60000)}-minute limit`
                            : 'Stopped without returning data'}
                          {failureAdvice(j.reportId) ? ` — ${failureAdvice(j.reportId)}` : ''}
                        </span>
                      )}
                      {j.effectiveState === 'expired' && 'Result is no longer stored'}
                      {/*
                        * `scheduled` had NO subline at all — `ACTIVE_STATES`
                        * excludes it and there was no branch for it, so a
                        * queued-for-later run rendered a clock icon, a name, and an
                        * empty line. It is the one state whose whole point is WHEN.
                        */}
                      {j.effectiveState === 'scheduled' && (
                        j.scheduledFor
                          ? <>Runs {fmtWhen(j.scheduledFor, panelNow)} · {fmtInFuture(j.scheduledFor, panelNow) || 'due now'}</>
                          : 'Waiting to run'
                      )}
                    </div>
                    {active && (
                      <div
                        role="progressbar"
                        aria-label={`${j.displayName} progress`}
                        aria-valuenow={j.percent ?? 0}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        className="mt-1.5 h-1 overflow-hidden rounded-full bg-gray-100"
                      >
                        <div className="h-full rounded-full bg-cb-accent transition-all" style={{ width: `${j.percent ?? 0}%` }} />
                      </div>
                    )}
                    {canView && (
                      <Link
                        to={`/reports/${j.reportId}?jobId=${j.id}`}
                        role="menuitem"
                        onClick={() => { markRead(j.id); setOpen(false) }}
                        className="btn-cb-tertiary mt-1.5 inline-block font-semibold"
                      >
                        View report →
                      </Link>
                    )}
                    {j.effectiveState === 'failed' && j.reportId && (
                      <Link
                        to={`/reports/${j.reportId}`}
                        role="menuitem"
                        onClick={() => setOpen(false)}
                        className="btn-cb-tertiary mt-1.5 inline-block font-semibold"
                      >
                        Fix filters &amp; retry →
                      </Link>
                    )}
                  </div>
                  {unread && <span className="mt-2 h-[7px] w-[7px] shrink-0 rounded-full bg-cb-accent-text" aria-label="Unread" />}
                  <button
                    type="button"
                    aria-label={`Dismiss ${j.displayName}`}
                    onClick={() => dismiss(j.id)}
                    className="shrink-0 rounded text-lg leading-none text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-cb-accent/40"
                  >
                    ×
                  </button>
                </div>
              )
            })}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-2.5">
            <Link to={QUEUE_PATH} onClick={() => setOpen(false)} className="btn-cb-tertiary font-semibold">
              View everything in the queue →
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationCenter
